import { useEffect, useMemo, useState } from "react";
import type { ResultDetail, ResultSummary } from "../../../shared/types";
import { Ico } from "../../components/shell";
import { AtlShell, QuizMark, ATL_ANS } from "../../components/atelier";
import { api, ApiError } from "../../api";
import { alertDialog, confirmDialog } from "../../components/Dialog";
import { navigate } from "../../router";

function whenLabel(ts: number): string {
  const d = new Date(ts);
  // Built by hand rather than via toLocaleString: the locale form ("… at …")
  // is long enough to wrap inside a results card.
  const date = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${date} · ${time}`;
}

/* ── Atelier loading / empty / error states ── */
function AtlLoading() {
  return (
    <AtlShell active="Results" title="Results & Insights">
      <div style={{ flex: 1, display: "grid", placeItems: "center", color: "var(--atl-faint)" }}>
        <div className="atl-eyebrow">Loading…</div>
      </div>
    </AtlShell>
  );
}

export default function Results({ resultId }: { resultId?: string }) {
  if (resultId) return <ResultDetailView id={resultId} />;
  return <ResultsList />;
}

/* ── List of saved sessions ── */
function ResultsList() {
  const [results, setResults] = useState<ResultSummary[] | null>(null);

  function load() {
    api.listResults().then((r) => setResults(r.results)).catch(() => setResults([]));
  }

  useEffect(load, []);

  async function del(r: ResultSummary, e: React.MouseEvent) {
    e.stopPropagation();
    const ok = await confirmDialog({
      title: "Delete results?",
      message: `The results for “${r.quizTitle}” will be permanently deleted. This cannot be undone.`,
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.deleteResult(r.id);
      setResults((cur) => (cur ? cur.filter((x) => x.id !== r.id) : cur));
    } catch (err) {
      alertDialog(err instanceof ApiError ? err.message : "Could not delete these results");
    }
  }

  if (results === null) return <AtlLoading />;

  return (
    <AtlShell
      active="Results"
      title="Results & Insights"
      sub={`${results.length} SESSION${results.length === 1 ? "" : "S"} SAVED`}
    >
      <div style={{ padding: 30, flex: 1, overflow: "auto" }}>
        {results.length === 0 ? (
          <div style={{ display: "grid", placeItems: "center", minHeight: "60vh", textAlign: "center" }}>
            <div style={{ maxWidth: 380 }}>
              <h2 className="atl-serif" style={{ fontSize: 27 }}>No sessions baked yet</h2>
              <p style={{ color: "var(--atl-soft)", fontWeight: 500, marginTop: 10, lineHeight: 1.55 }}>
                Host a live quiz and its podium, correct-rates and per-question insights will be saved here.
              </p>
              <button className="atl-btn" style={{ marginTop: 20 }} onClick={() => navigate("/")}>
                <Ico name="play" size={16} /> Host a quiz
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 18 }}>
            {results.map((r, i) => (
              <div
                key={r.id}
                className="atl-card"
                style={{ padding: 22, cursor: "pointer", display: "flex", flexDirection: "column", gap: 18 }}
                onClick={() => navigate(`/results/${r.id}`)}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
                  <QuizMark c={ATL_ANS[i % 4]} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      className="atl-serif"
                      style={{
                        fontSize: 17.5,
                        lineHeight: 1.25,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {r.quizTitle}
                    </div>
                    <div
                      className="atl-eyebrow"
                      style={{ marginTop: 6, letterSpacing: ".14em", whiteSpace: "nowrap" }}
                    >
                      {whenLabel(r.endedAt)}
                    </div>
                  </div>
                  <button
                    className="atl-iconbtn"
                    title="Delete results"
                    style={{ width: 30, height: 30, flex: "0 0 auto", color: "var(--atl-accent)" }}
                    onClick={(e) => del(r, e)}
                  >
                    ✕
                  </button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", marginTop: "auto" }}>
                  {[
                    [`${r.playerCount}`, "Teams"],
                    [`${r.avgCorrectPct}%`, "Avg correct"],
                    [`${r.questionCount}`, "Questions"],
                  ].map(([n, l], j) => (
                    <div
                      key={l}
                      style={{
                        minWidth: 0,
                        paddingLeft: j === 0 ? 0 : 14,
                        borderLeft: j === 0 ? "none" : "1px solid var(--atl-line)",
                      }}
                    >
                      <div
                        className="atl-serif"
                        style={{ fontSize: 24, color: j === 1 && r.avgCorrectPct < 70 ? "var(--atl-accent)" : "var(--atl-ink)" }}
                      >
                        {n}
                      </div>
                      {/* Tighter tracking than the default eyebrow so "Avg correct"
                          stays on one line in a three-up card. */}
                      <div
                        className="atl-eyebrow"
                        style={{ marginTop: 5, fontSize: 9.5, letterSpacing: ".1em", whiteSpace: "nowrap" }}
                      >
                        {l}
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    paddingTop: 16,
                    borderTop: "1px solid var(--atl-line)",
                  }}
                >
                  {r.topNickname ? (
                    <div className="atl-chip atl-chip--gold" style={{ minWidth: 0 }}>
                      <Ico name="trophy" size={13} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.topNickname}
                        {r.topScore != null && ` · ${r.topScore.toLocaleString()} pts`}
                      </span>
                    </div>
                  ) : (
                    <span className="atl-eyebrow">No winner recorded</span>
                  )}
                  <button
                    className="atl-btn atl-btn--link"
                    style={{ fontSize: 12.5, marginLeft: "auto", padding: "2px 2px", flex: "0 0 auto" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/results/${r.id}`);
                    }}
                  >
                    Insights →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AtlShell>
  );
}

/* ── Single session insights ── */
function ResultDetailView({ id }: { id: string }) {
  const [r, setR] = useState<ResultDetail | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.getResult(id).then((d) => setR(d.result)).catch(() => setMissing(true));
  }, [id]);

  const catPerf = useMemo(() => {
    if (!r) return [];
    const m = new Map<string, { sum: number; n: number }>();
    for (const q of r.questions) {
      const c = q.category || "Uncategorised";
      const e = m.get(c) ?? { sum: 0, n: 0 };
      e.sum += q.correctPct;
      e.n += 1;
      m.set(c, e);
    }
    return [...m.entries()].map(([c, e]) => [c, Math.round(e.sum / e.n)] as [string, number]);
  }, [r]);

  async function del() {
    if (!r) return;
    const ok = await confirmDialog({
      title: "Delete results?",
      message: `The results for “${r.quizTitle}” will be permanently deleted. This cannot be undone.`,
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.deleteResult(r.id);
      navigate("/results");
    } catch (err) {
      alertDialog(err instanceof ApiError ? err.message : "Could not delete these results");
    }
  }

  if (missing) return <ResultsList />;
  if (!r) return <AtlLoading />;

  const missed = [...r.questions].sort((a, b) => a.correctPct - b.correctPct).slice(0, 5);
  const hardest = missed[0];
  const kpis = [
    { n: `${r.playerCount}`, l: "Teams competed" },
    { n: `${r.avgCorrectPct}%`, l: "Avg correct-rate" },
    { n: `${r.questionCount}`, l: "Questions" },
    { n: hardest ? `Q${hardest.position + 1}` : "—", l: "Hardest question" },
  ];

  return (
    <AtlShell
      active="Results"
      title={r.quizTitle}
      sub={`${whenLabel(r.endedAt)} · PIN ${r.pin}`}
      actions={
        <>
          <button className="atl-btn atl-btn--ghost" onClick={() => navigate("/results")}>
            ← All results
          </button>
          <button className="atl-btn atl-btn--ghost" style={{ color: "var(--atl-accent)" }} onClick={del}>
            <Ico name="export" size={15} /> Delete
          </button>
        </>
      }
    >
      <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 16, flex: 1, overflow: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 14 }}>
          {kpis.map((k, i) => (
            <div key={k.l} className="atl-card" style={{ padding: "18px 22px" }}>
              <div className="atl-serif" style={{ fontSize: 30, color: i === 1 ? "var(--atl-accent)" : "var(--atl-ink)" }}>
                {k.n}
              </div>
              <div className="atl-eyebrow" style={{ marginTop: 5 }}>{k.l}</div>
            </div>
          ))}
        </div>

        <div className="atl-respond" style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 16 }}>
          <div className="atl-card" style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 18 }}>
              <h3 className="atl-serif" style={{ fontSize: 19 }}>Most missed questions</h3>
              <span className="atl-eyebrow" style={{ marginLeft: "auto" }}>% CORRECT</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {missed.map((m) => (
                <div key={m.position}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <span className="atl-mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--atl-faint)", flex: "0 0 auto" }}>
                      Q{m.position + 1}
                    </span>
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: 14,
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {m.prompt}
                    </span>
                    {m.category && (
                      <span className="atl-chip" style={{ fontSize: 10.5, padding: "2px 9px" }}>{m.category}</span>
                    )}
                    <span
                      className="atl-mono"
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: m.correctPct < 55 ? "var(--atl-accent)" : "var(--atl-ink)",
                        width: 40,
                        textAlign: "right",
                      }}
                    >
                      {m.correctPct}%
                    </span>
                  </div>
                  <div className="atl-bar">
                    <i
                      style={{
                        width: `${m.correctPct}%`,
                        background:
                          m.correctPct < 55 ? "var(--atl-accent)" : m.correctPct < 70 ? "var(--atl-gold)" : "var(--atl-c)",
                      }}
                    />
                  </div>
                </div>
              ))}
              {missed.length === 0 && (
                <div className="atl-eyebrow">No answers recorded.</div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="atl-card" style={{ padding: 22, background: "var(--atl-surface-2)" }}>
              <h3 className="atl-serif" style={{ fontSize: 17, marginBottom: 14 }}>Final standings</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                {r.standings.slice(0, 6).map((t) => (
                  <div key={t.nickname} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span
                      className="atl-serif"
                      style={{ width: 16, fontSize: 15, color: t.rank === 1 ? "var(--atl-gold)" : "var(--atl-faint)" }}
                    >
                      {t.rank}
                    </span>
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: 13.5,
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t.emoji ? `${t.emoji} ` : ""}{t.nickname}
                    </span>
                    <span className="atl-mono" style={{ fontSize: 13, fontWeight: 700 }}>{t.score.toLocaleString()}</span>
                  </div>
                ))}
                {r.standings.length === 0 && (
                  <div className="atl-eyebrow">No teams played.</div>
                )}
              </div>
            </div>
            <div className="atl-card" style={{ padding: 22, background: "var(--atl-ink)", color: "#fff", flex: 1 }}>
              <div className="atl-eyebrow" style={{ color: "var(--atl-gold-soft)", marginBottom: 10 }}>
                TRAINING OPPORTUNITY
              </div>
              <p style={{ fontSize: 13.5, lineHeight: 1.55, color: "rgba(255,255,255,.9)", fontWeight: 500 }}>
                {hardest ? (
                  <>
                    <span className="atl-serif" style={{ fontStyle: "italic" }}>
                      {hardest.category || hardest.prompt.slice(0, 28)}
                    </span>{" "}
                    landed at {hardest.correctPct}% — worth a quick refresher.
                  </>
                ) : (
                  <>Run a session to surface where the team needs a refresher.</>
                )}
              </p>
            </div>
          </div>
        </div>

        {catPerf.length > 0 && (
          <div className="atl-card" style={{ padding: 24 }}>
            <h3 className="atl-serif" style={{ fontSize: 19, marginBottom: 18 }}>Category performance</h3>
            <div style={{ display: "flex", gap: 22, alignItems: "flex-end", height: 116 }}>
              {catPerf.map(([c, v]) => (
                <div
                  key={c}
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                    height: "100%",
                    justifyContent: "flex-end",
                  }}
                >
                  <span className="atl-mono" style={{ fontWeight: 700, fontSize: 13.5 }}>{v}%</span>
                  <div
                    style={{
                      width: "100%",
                      maxWidth: 84,
                      height: `${Math.max(4, v)}%`,
                      borderRadius: "8px 8px 0 0",
                      background: v < 70 ? "var(--atl-accent)" : "var(--atl-gold)",
                    }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 500, color: "var(--atl-soft)", textAlign: "center" }}>{c}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AtlShell>
  );
}
